import clsx from 'clsx';
import type { ReactNode } from 'react';

export interface StatusBadgeProps {
  status: string;
  variant?: 'document' | 'health' | 'task';
  /**
   * Optional explicit tooltip text (ctsk-072). When set, overrides the
   * default `${variant}: ${status}` text — useful when the variant is
   * the wrong noun for the row (e.g. a work item status badge that
   * should read "CT-12: done", not "document: done").
   */
  title?: string;
  /**
   * Optional icon node rendered before the status text (0.1.6). When
   * present, the generic `●` dot (.badge::before) is suppressed via
   * the `.badge--with-icon` modifier so we don't show two prefixes.
   * Used by the work-items list to differentiate statuses at a
   * glance: ✓ done, ⏵ in progress, ⏸ waiting, ⊘ blocked, …
   */
  icon?: ReactNode;
  /**
   * Optional pretty label (e.g. "In progress" for status "in_progress").
   * Falls back to the raw status string when not provided. Used by
   * the work-items list with `WORK_STATUS_LABELS` so the badge reads
   * like a label, not a key.
   */
  label?: string;
  /**
   * When provided, the badge renders as a clickable <button> with the
   * same visual style. Used by WorkItemsList to let row badges narrow
   * the list to that status in one click — mirrors the TagBadge
   * clickable variant (ctsk-077). stopPropagation prevents the row's
   * own onClick (which would navigate to the detail page) from firing
   * underneath. The tooltip switches to "Filter by status: <label>".
   */
  onClick?: (status: string) => void;
}

export function StatusBadge({ status, variant = 'document', title, icon, label, onClick }: StatusBadgeProps) {
  const displayLabel = label ?? status;

  if (onClick) {
    return (
      <button
        type="button"
        className={clsx('badge', `badge--${status}`, 'badge--clickable', icon && 'badge--with-icon')}
        title={title ?? `Filter by ${variant}: ${displayLabel}`}
        onClick={(e) => { e.stopPropagation(); onClick(status); }}
      >
        {icon}
        {displayLabel}
      </button>
    );
  }

  return (
    <span
      className={clsx('badge', `badge--${status}`, icon && 'badge--with-icon')}
      title={title ?? `${variant}: ${status}`}
    >
      {icon}
      {displayLabel}
    </span>
  );
}
